import React, { useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { ArrowRight, Bell, Package, ShieldCheck } from "lucide-react";
import { track } from "@/lib/analytics";
import { PRE_HARVEST_MODE } from "@/lib/siteMode";
import WaitlistForm from "../WaitlistForm";

const BOTTLE_IMG =
  "https://i.ibb.co/N6S63tvr/Botella-fondo-claro.png";
const BOX_IMG =
  "https://i.ibb.co/jvmzqrcj/Caja-con-dos-botellas-fondo-oscuro-premium-estrecho.png";

const TRUST_ICONS = [Package, Bell, ShieldCheck, Package];

export default function Shop() {
  const { t, lang } = useLang();
  const trustItems = t("shop.trust_items");
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitlistProduct, setWaitlistProduct] = useState("");

  function openWaitlist(productSlug = "") {
    track("cta_click", { cta: "shop_waitlist", product: productSlug, location: "home_shop" });
    setWaitlistProduct(productSlug);
    setShowWaitlist(true);
  }

  const cards = [
    {
      slug: "botella-500ml",
      href: "/producto/botella-500ml",
      img: BOTTLE_IMG,
      tag: lang === "es" ? "Botella individual" : "Single bottle",
      title: lang === "es" ? "AOVE · 500 ml" : "EVOO · 500 ml",
      subtitle: lang === "es" ? "Cosecha temprana" : "Early harvest",
      testId: "home-shop-bottle",
    },
    {
      slug: "estuche-regalo",
      href: "/producto/estuche-madera-2x500ml",
      img: BOX_IMG,
      tag: lang === "es" ? "Edición regalo" : "Gift edition",
      title:
        lang === "es"
          ? "Caja de madera con motivo del cortijo · 2 × 500 ml"
          : "Wooden presentation box with estate motif · 2 × 500 ml",
      subtitle:
        lang === "es"
          ? "Caja de madera con motivo del cortijo"
          : "Wooden presentation box with estate motif",
      testId: "home-shop-box",
    },
  ];

  const preHarvestBadge = lang === "es"
    ? "Próxima cosecha · aún no disponible"
    : "Next harvest · not yet available";
  const waitlistCta = lang === "es"
    ? "Avísame cuando esté disponible"
    : "Notify me when it is available";
  const shopCta = lang === "es" ? "Ver todos los formatos" : "See the full shop";

  return (
    <section
      id="pedido"
      data-testid="shop-section"
      className="relative bg-cream-200 py-24 sm:py-32 lg:py-40"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="max-w-2xl">
          <span className="text-[11px] uppercase tracking-[0.32em] text-gold-deep font-medium">
            {t("shop.overline")}
          </span>
          <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] text-olive-900 font-medium">
            {t("shop.h2_a")} <span className="italic">{t("shop.h2_b")}</span>.
          </h2>
          <p className="mt-6 text-base sm:text-lg text-olive-800/85 font-light leading-relaxed">
            {PRE_HARVEST_MODE
              ? lang === "es"
                ? "La próxima cosecha está en preparación. Déjanos tu email para que te avisemos en cuanto la nueva edición esté disponible."
                : "The next harvest is in preparation. Leave your email and we will notify you when the new edition is available."
              : t("shop.intro")}
          </p>
        </div>

        <div className="mt-16 lg:mt-20 grid md:grid-cols-2 gap-6 lg:gap-10">
          {cards.map((c) => (
            <div
              key={c.href}
              data-testid={c.testId}
              className="relative flex flex-col bg-cream-100 border border-cream-400"
            >
              {PRE_HARVEST_MODE && (
                <div className="absolute -top-3 left-6 z-10 inline-flex items-center gap-2 bg-stone-700 text-cream-100 px-3 py-1 text-[10px] uppercase tracking-[0.24em]">
                  {preHarvestBadge}
                </div>
              )}
              <div className="aspect-[4/5] bg-cream-200 overflow-hidden border-b border-cream-400">
                <img
                  src={c.img}
                  alt={c.title}
                  loading="lazy"
                  className="h-full w-full object-contain p-8 sm:p-14"
                />
              </div>
              <div className="p-6 sm:p-8 flex flex-col flex-1">
                <div className="text-[10px] uppercase tracking-[0.28em] text-gold-deep">{c.tag}</div>
                <h3 className="mt-3 font-serif text-2xl sm:text-3xl text-olive-900 leading-tight">
                  {c.title}
                </h3>
                <p className="mt-1 text-sm text-olive-800/70 font-light">{c.subtitle}</p>
                <div className="mt-6 pt-6 border-t border-cream-400 flex items-end justify-between">
                  {PRE_HARVEST_MODE ? (
                    <button
                      type="button"
                      onClick={() => openWaitlist(c.slug)}
                      data-testid={`${c.testId}-waitlist-btn`}
                      className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-olive-800 hover:text-gold-deep transition-colors"
                    >
                      <Bell size={13} strokeWidth={1.4} />
                      {waitlistCta}
                    </button>
                  ) : (
                    <a
                      href={c.href}
                      onClick={() => track("cta_click", { cta: c.testId, location: "home_shop" })}
                      className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.26em] text-olive-800 hover:text-gold-deep"
                    >
                      {t("shop_page.view_product")}
                      <ArrowRight size={14} strokeWidth={1.3} />
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Big CTA */}
        <div className="mt-12 flex justify-center">
          {PRE_HARVEST_MODE ? (
            <button
              type="button"
              onClick={() => openWaitlist()}
              data-testid="home-shop-cta"
              className="inline-flex items-center gap-3 px-10 py-5 bg-olive-700 text-cream-100 text-[12px] uppercase tracking-[0.26em] hover:bg-olive-800 transition-colors group"
            >
              <Bell size={16} strokeWidth={1.2} />
              {waitlistCta}
            </button>
          ) : (
            <a
              href="/tienda"
              data-testid="home-shop-cta"
              onClick={() => track("cta_click", { cta: "home_full_shop", location: "home_shop" })}
              className="inline-flex items-center gap-3 px-10 py-5 bg-olive-700 text-cream-100 text-[12px] uppercase tracking-[0.26em] hover:bg-olive-800 transition-colors group"
            >
              {shopCta}
              <ArrowRight size={16} strokeWidth={1.2} className="transition-transform group-hover:translate-x-1" />
            </a>
          )}
        </div>

        {/* Trust row */}
        <div className="mt-20 lg:mt-24">
          <div className="text-[11px] uppercase tracking-[0.32em] text-gold-deep font-medium mb-6">
            {t("shop.trust_title")}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 border-t border-olive-700/20">
            {trustItems.map((item, i) => {
              const Icon = TRUST_ICONS[i] || Package;
              return (
                <div
                  key={item.k}
                  data-testid={`shop-trust-${i}`}
                  className="p-6 lg:p-8 border-b border-olive-700/20 sm:[&:nth-child(2n)]:border-l lg:sm:[&:nth-child(2n)]:border-l-0 lg:[&:not(:first-child)]:border-l border-olive-700/20"
                >
                  <Icon size={22} strokeWidth={1.2} className="text-gold-deep" />
                  <div className="mt-4 font-serif text-lg text-olive-900">{item.k}</div>
                  <div className="mt-1 text-sm text-olive-800/75 font-light leading-relaxed">{item.v}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {showWaitlist && (
        <WaitlistForm
          onClose={() => setShowWaitlist(false)}
          preselectedProduct={waitlistProduct}
        />
      )}
    </section>
  );
}

